<template>
  <div class="settings-content">
    <CompanyProfile v-show="currentTabActive == 1" />
    <ServicesConfiguration v-show="currentTabActive == 2" />
    <EmployeeConfiguration v-show="currentTabActive == 3" />
    <ScheduleConfiguration v-show="currentTabActive == 4" />
    <AccountSettings v-show="currentTabActive == 5" />
  </div>
</template>
<script setup lang="ts">
import CompanyProfile from "./settings/company-profile.vue";
import EmployeeConfiguration from "./settings/employee-configuration.vue";
import ServicesConfiguration from "./settings/services-configuration.vue";
import ScheduleConfiguration from "./settings/schedule-configuration.vue";
import AccountSettings from "./settings/account-settings.vue";

defineProps<{
  currentTabActive: number;
}>();
</script>

<style lang="scss">
@use "../styles/_breakpoints" as bp;

.settings-content {
  padding: 32px;
  display: flex;
  height: 100%;
  overflow: scroll;

  @include bp.tablet {
    padding: 16px;
  }
}

.table-view {
  width: 100%;

  &__table {
    width: 100%;
    position: relative;
    padding-top: 55px;
    overflow: scroll;
    height: calc(100% - 50px);

    @include bp.tablet {
      // The header below becomes a stacked-card label instead of an
      // absolutely-positioned overlay bar, so no reserved space is needed.
      padding-top: 0;
    }
  }

  &__name {
    min-width: 200px;
    font-family: "Inter", sans-serif;
    text-align: left !important;

    @include bp.tablet {
      min-width: 0;
    }

    h3 {
      color: #1f2937;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
    }
    p {
      color: #4b5563;
      font-size: 12px;
      font-weight: 500;
      line-height: 12px;
      margin-bottom: 0;
    }
  }

  &__header {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    padding: 0 !important;
    color: #1f2937;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    border-radius: 8px;
    background: #f3f4f6;

    div {
      padding: 16px;
      vertical-align: middle;
    }

    // This header bar is a multi-column overlay aligned to the body's grid
    // columns - once the body stacks to one column below, it can no longer
    // line up with anything, so each stacked cell instead gets its own
    // inline label via [data-label]::before (see &__body below).
    @include bp.tablet {
      display: none;
    }
  }

  &__body {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: auto;
    gap: 16px;
    text-align: center;

    color: #1f2937;

    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;

    @include bp.tablet {
      grid-template-columns: 1fr;
      text-align: left;
      gap: 4px;
    }

    // Opt-in per-cell mobile label - set `:data-label="$t('price')"` (etc.)
    // on a cell in the component's own template where the value alone
    // wouldn't be self-explanatory once stacked (a name/title or an action
    // button doesn't need one).
    & > div[data-label]::before {
      content: attr(data-label) ": ";
      display: none;
      color: #6b7280;
      font-size: 12px;
      font-weight: 600;

      @include bp.tablet {
        display: inline;
      }
    }

    & > div {
      padding: 16px;
      vertical-align: middle;

      @include bp.tablet {
        padding: 6px 4px;
      }
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    column-gap: 15px;

    @include bp.tablet {
      justify-content: flex-start;
      // Visually closes out each stacked row-group of cells, since they're
      // no longer aligned into columns that make the grouping obvious.
      padding-top: 0 !important;
      padding-bottom: 16px !important;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 8px;
    }

    .v-btn {
      border-radius: 10px;
      background: #403838;
      color: #fff;
      padding: 14px 24px;
      text-align: center;
      font-family: "Poppins", sans-serif;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 12px;
    }
  }

  &__add {
    border-radius: 8px;
    background: #f3f4f6;
    box-shadow: none;
    color: #1f2937;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 14px;
  }

  &__row {
    display: flex;
    justify-content: space-between;

    div {
      padding: 16px;
      text-align: center;
    }
  }
}
</style>
